import { createSelector } from '@reduxjs/toolkit';
import { isActive, isCloseOut, isComingSoon, isGrandOpening } from 'services/marketingStatusValidator';
import TypeaheadLocationType from 'enums/typeaheadLocationType';
import { getMovingDateFilterApplied, isMoveInDateApplied } from '../services/moveInDateService';
import ProductType from '../enums/productType';
import { hasModelHome } from '../services/homeStatusValidator';
import LocationType from '../enums/locationType';

const selectAdult = (state) => state?.searchParameters?.features?.adult;
const selectAgeRestricted = (state) => state?.searchParameters?.features?.ageRestricted;
const isCustom = (state) => state?.searchParameters?.product?.productType === ProductType.Custom.toString();
const selectBrands = (state) => state?.searchParameters?.counts?.brands;
const selectFetchResultCountsStatus = (state) => state?.searchParameters?.fetchResultCountsStatus;
const selectCommunityName = (state) => state?.searchParameters?.facts?.communityName;
const selectMarketName = (state) => state?.searchParameters?.location?.marketName;
const selectStateAbbreviation = (state) => state?.searchParameters?.location?.marketState;
const selectZip = (state) => state?.searchParameters?.location?.zip;
const selectCounty = (state) => state?.searchParameters?.location?.county;
const selectContentTemplateType = (state) => state?.searchParameters?.contentTemplateType;
const selectHotDealsCount = (state) => state?.searchParameters?.counts?.hotDealsCount;
const selectAdultCount = (state) => state?.searchParameters?.counts?.adultCount;
const selectComingSoonCount = (state) => state?.searchParameters?.counts?.comingSoonCount;
const selectQuickMoveInCount = (state) => state?.searchParameters?.counts?.quickMoveInCount;
const selectBuildablePlansCount = (state) => state?.searchParameters?.counts?.buildablePlansCount;
const selectMultiFamilyCount = (state) => state?.searchParameters?.counts?.multiFamilyCount;
const isBuildOnYourLot = (state) =>
  state?.searchParameters?.product?.productType === ProductType.BuildOnYourLot.toString();
const isCustomHomes = createSelector(isCustom, isBuildOnYourLot, (custom, buildOnYourLot) =>
  Boolean(custom, buildOnYourLot),
);

export const selectMarketId = (state) => state?.searchParameters?.location?.marketId;
export const selectPriceReduction = (state) => state?.searchParameters?.facts?.priceReduction;
export const selectMoveInDateRange = (state) => state?.searchParameters?.facts?.moveInDateRange;
export const selectMinimumPrice = (state) => state?.searchParameters?.facts?.price?.minimumPrice;
export const selectMaximumPrice = (state) => state?.searchParameters?.facts?.price?.maximumPrice;
export const selectNatureAreas = (state) => state?.searchParameters?.features?.natureAreas;
export const selectPool = (state) => state?.searchParameters?.features?.pool;
export const selectGolfCourse = (state) => state?.searchParameters?.features?.golfCourse;
export const selectGreen = (state) => state?.searchParameters?.features?.green;
export const selectSportsFacilities = (state) => state?.searchParameters?.features?.sportsFacilities;
export const selectParks = (state) => state?.searchParameters?.features?.parks;
export const selectViews = (state) => state?.searchParameters?.features?.views;
export const selectWaterfront = (state) => state?.searchParameters?.features?.waterfront;
export const selectGatedCommunity = (state) => state?.searchParameters?.features?.gatedCommunity;
export const selectMarketingStatus = (state) => state?.searchParameters?.facts?.marketingStatus;
export const selectConsumerPromos = (state) => state?.searchParameters?.facts?.consumerPromos;
export const selectRvGarage = (state) => state?.searchParameters?.facts?.recreationalVehicleGarage;
export const selectHomeStatus = (state) => state?.searchParameters?.facts?.homeStatus;
export const selectBathrooms = (state) => state?.searchParameters?.facts?.minimumBathroom;
export const selectBedrooms = (state) => state?.searchParameters?.facts?.minimumBedroom;
export const selectSchools = (state) => state?.searchParameters?.features?.schoolDistrictIds;
export const selectBrandId = (state) => state?.searchParameters?.facts?.brandId;
export const selectBrandName = (state) => state?.searchParameters?.facts?.brandName;
export const selectCity = (state) => state?.searchParameters?.location?.city;
export const selectLivingAreas = (state) => state?.searchParameters?.facts?.livingAreas;
export const selectStories = (state) => state?.searchParameters?.facts?.stories;
export const selectPrimaryBedroom = (state) => state?.searchParameters?.facts?.primaryBedroom;
export const selectGarages = (state) => state?.searchParameters?.facts?.garages;
export const selectSingleFamily = (state) => state?.searchParameters?.features?.singleFamily;
export const selectLuxury = (state) => state?.searchParameters?.features?.luxury;
export const selectCondosAndTownHomes = (state) => state?.searchParameters?.features?.condosAndTownHomes;
export const selectPage = (state) => Number(state?.searchParameters?.results?.paging?.page);
export const selectSpotlightListingsCount = (state) => Number(state?.searchParameters?.counts?.spotlightListingsCount);
export const selectPaging = (state) => state?.searchParameters?.results?.paging;
export const selectPrice = (state) => state?.searchParameters?.facts?.price;
export const selectAppliedFacets = (state) => state?.facetsState?.appliedFilters;
export const selectListingCount = (state) => state?.searchParameters?.counts?.listingCount;
export const selectHomesCount = (state) => state?.searchParameters?.counts?.homesCount;
export const selectProductType = (state) => state?.searchParameters?.product?.productType;
export const selectLocation = (state) => state?.searchParameters?.location;
export const selectProduct = (state) => state?.searchParameters?.product;
export const selectResults = (state) => state?.searchParameters?.results;
export const selectFeatures = (state) => state?.searchParameters?.features;
export const selectFacts = (state) => state?.searchParameters?.facts;
export const selectResultsType = (state) => state?.searchParameters?.results.resultType;
export const selectSchoolDistrictName = (state) => state?.searchParameters?.location?.schoolDistrictName;
export const selectResultsModifiers = (state) => state?.searchParameters?.resultsModifiers;
export const selectFetchResultListingsStatus = (state) => state?.searchParameters?.fetchResultListingsStatus;
export const selectResultType = (state) => state?.searchParameters?.results?.resultType;
export const selectLocationType = (state) => state?.searchParameters?.location?.locationType;
export const selectSearchParameters = (state) => state?.searchParameters;
export const selectSorting = (state) => state?.searchParameters?.results?.sorting;
export const selectPageSize = (state) => Number(state?.searchParameters?.results?.paging?.pageSize);
export const selectReviews = (state) => state?.searchParameters?.facts?.builderReviews;
export const isNotCommunityLocationType = (state) =>
  state?.searchParameters?.location?.locationType !== TypeaheadLocationType.Community.toString();
export const isLocationTypeState = (state) =>
  state?.searchParameters?.location?.locationType === LocationType.State.toString();
export const selectedMarkerId = (state) => state?.mapState?.selectedMarker?.id;
export const selectHomeOwnersAssociationFee = (state) => state?.searchParameters?.facts?.hasHomeOwnersAssociationFee;
export const selectFetchBrandsFacetingStatus = (state) => state?.searchParameters?.fetchBrandsFacetingStatus;

// Selector used to get the values from the brand as the ID and Name.
const selectBrandInformation = createSelector(selectBrandId, selectBrandName, (brandId, brandName) => ({
  brandId,
  brandName,
}));

/**
 * Selects the value of the excludeAdult feature from the search parameters in the Redux store state.
 * @param {Object} state - The current state of the application.
 * @returns {boolean} - The value of the excludeAdult parameter.
 */
const selectExcludeAdult = (state) => state?.searchParameters?.resultsModifiers?.excludeAdult;

/**
 * Selects the value of the excludeAgeRestricted feature from the search parameters in the state.
 * @param {Object} state - The current state of the application.
 * @returns {boolean} - The value of the excludeAgeRestricted parameter.
 */
const selectExcludeAgeRestricted = (state) => state?.searchParameters?.resultsModifiers?.excludeAgeRestricted;

/**
 * This selector aims to expose all the properties that we have in the housing options section in the All filters menu.
 */
const housingOptionsSelector = createSelector(
  selectSingleFamily,
  selectLuxury,
  selectCondosAndTownHomes,
  isCustomHomes,
  selectHomeStatus,
  (singleFamily, luxury, condosAndTownHomes, custom, homeStatus) => {
    const filters = { singleFamily, luxury, condosAndTownHomes, custom, model_homes: hasModelHome(homeStatus) };
    return filters;
  },
);

/**
 * This selector aims to expose all the properties that we have in the community status selector
 * in the All filters menu.
 */
const communityStatusSelector = createSelector(selectMarketingStatus, (marketingStatus) => {
  const filters = {
    coming_soon: isComingSoon(marketingStatus),
    grand_opening: isGrandOpening(marketingStatus),
    active: isActive(marketingStatus),
    closeout: isCloseOut(marketingStatus),
  };
  return filters;
});

/**
 * Selects information about listing counts and result type for the Advanced Filters confirmation button.
 */
export const selectCountsAndResultType = createSelector(
  selectListingCount,
  selectFetchResultListingsStatus,
  selectResultType,
  (listingCounts, resultCountsStatus, resultType) => ({
    listingCounts,
    resultCountsStatus,
    resultType,
  }),
);

/**
 * This selector aims to expose the flag used to identify the neighborhood search.
 */
export const selectIsNeighborhoodType = createSelector(
  selectLocationType,
  (locationType) => locationType === LocationType.Neighborhood.toString(),
);

/**
 * This selector aims to expose the flag used to identify the school district search.
 */
export const selectIsSchoolDistrictType = createSelector(
  selectLocationType,
  (locationType) => locationType === LocationType.SchoolDistrict.toString(),
);

/**
 * This selector aims to expose the filtered brandId (if any) and the list of schools (if any) properties.
 */
const brandIdAndSchoolsSelector = createSelector(selectSchools, selectBrandId, (schools, brandId) => {
  const filters = { schools, brandId: Number(brandId) };
  return filters;
});

/**
 * Returns an object with properties indicating the active adult communities based on the selected filters.
 *
 * @function
 * @name activeAdultCommunitiesSelector
 * @param {boolean} selectAdult - Indicates if adult communities are selected.
 * @param {boolean} selectAgeRestricted - Indicates if age-restricted communities are selected.
 * @param {boolean} selectExcludeAdult - Indicates if adult communities are excluded.
 * @param {boolean} selectExcludeAgeRestricted - Indicates if age-restricted communities are excluded.
 * @returns {Object} Object indicating the 55+ communities status based on the selected filters.
 */
export const activeAdultCommunitiesSelector = createSelector(
  selectAdult,
  selectAgeRestricted,
  selectExcludeAdult,
  selectExcludeAgeRestricted,
  (adult, ageRestricted, excludeAdult, excludeAgeRestricted) => {
    const only = adult === true && ageRestricted === true;
    const exclude = excludeAdult === true && excludeAgeRestricted === true;

    return {
      only,
      exclude,
      any: !only && !exclude,
    };
  },
);

/**
 * This selector aims to expose all the properties that we have in the other filters section in the All filters menu.
 * Such as; only with reviews, hotdeal and the different states of Active adult communities.
 */
const otherFiltersSelector = createSelector(
  selectReviews,
  selectConsumerPromos,
  selectPriceReduction,
  selectHomeOwnersAssociationFee,
  activeAdultCommunitiesSelector,
  (reviews, consumerPromos, priceReduction, hasHomeOwnersAssociationFee, activeAdultCommunities) => {
    const { only, exclude, any } = activeAdultCommunities;
    const filters = {
      reviews,
      hotDeals: Boolean(consumerPromos),
      priceReduction: Boolean(priceReduction),
      hasHomeOwnersAssociationFee: Boolean(hasHomeOwnersAssociationFee),
      any,
      only,
      exclude,
    };
    return filters;
  },
);

export const selectFetchCountsStatusAndsCounts = createSelector(
  selectListingCount,
  selectFetchResultCountsStatus,
  (listingCounts, resultCountsStatus) => ({ listingCounts, resultCountsStatus }),
);

export const fetchingResultsCallStatusesSelector = createSelector(
  selectFetchResultListingsStatus,
  selectFetchResultCountsStatus,
  (fetchResultListingsStatus, fetchResultCountsStatus) => ({ fetchResultListingsStatus, fetchResultCountsStatus }),
);

export const selectSnippetCommand = createSelector(
  selectFacts,
  selectLocation,
  selectProduct,
  selectResultType,
  selectContentTemplateType,
  (facts, location, product, resultType, contentTemplateType) => ({
    facts,
    location,
    product,
    results: { resultType },
    contentTemplateType,
  }),
);

export const selectProductAndResultType = createSelector(
  selectProductType,
  selectResultType,
  (productType, resultType) => ({ productType, resultType }),
);

export const selectSearchCount = createSelector(
  selectHotDealsCount,
  selectAdultCount,
  selectComingSoonCount,
  selectQuickMoveInCount,
  selectBuildablePlansCount,
  selectMultiFamilyCount,
  (hotDealsCount, adultCount, comingSoonCount, quickMoveInCount, buildablePlansCount, multiFamilyCount) => ({
    hotDealsCount,
    adultCount,
    comingSoonCount,
    quickMoveInCount,
    buildablePlansCount,
    multiFamilyCount,
  }),
);

export const selectSearchCommandContext = createSelector(selectSearchParameters, (searchParameters) => ({
  searchParameters,
}));

export const selectLocationContentContext = createSelector(
  selectLocationType,
  selectMarketId,
  selectMarketName,
  selectCity,
  selectCounty,
  selectStateAbbreviation,
  selectZip,
  selectProductType,
  selectBrandId,
  selectBrandName,
  selectCommunityName,
  selectResultType,
  selectPage,
  selectSportsFacilities,
  selectMoveInDateRange,
  selectSchoolDistrictName,
  (
    geoLocationType,
    marketId,
    marketName,
    city,
    county,
    stateAbbreviation,
    zip,
    productType,
    brandId,
    brandName,
    communityName,
    resultType,
    pageNumber,
    sportsFacilities,
    moveInDateRange,
    schoolDistrictName,
  ) => ({
    geoLocationType,
    marketId,
    marketName,
    city,
    county,
    stateAbbreviation,
    zip,
    productType,
    brandId,
    brandName,
    communityName,
    resultType,
    pageNumber,
    sportsFacilities,
    moveInDateRange,
    schoolDistrictName,
  }),
);

export const selectSubdirectory = createSelector(
  selectResultsType,
  selectCondosAndTownHomes,
  selectSingleFamily,
  selectHomeStatus,
  isCustom,
  isBuildOnYourLot,
  selectLocationType,
  (resultType, condosAndTownHomes, singleFamily, homeStatus, custom, buildOnYourLot, locationType) => ({
    resultType,
    condosAndTownHomes,
    singleFamily,
    homeStatus,
    custom,
    buildOnYourLot,
    locationType,
  }),
);

export const selectBrand = createSelector(
  selectBrandName,
  selectBrandId,
  selectResultType,
  (brandName, brandId, resultType) => ({
    brandName,
    brandId,
    resultType,
  }),
);

export const selectBrandFilterContext = createSelector(selectBrands, selectBrandId, (brands, brandId) => ({
  brands,
  brandId,
}));

// selector created to tap into the features object in the store
// and return the current values of the community amenities.
export const communityAmenitiesSelector = createSelector(
  selectNatureAreas,
  selectPool,
  selectGolfCourse,
  selectGreen,
  selectSportsFacilities,
  selectParks,
  selectViews,
  selectWaterfront,
  selectGatedCommunity,
  (natureAreas, pool, golfCourse, green, sportsFacilities, parks, views, waterfront, gatedCommunity) => ({
    natureAreas,
    pool,
    golfCourse,
    green,
    sportsFacilities,
    parks,
    views,
    waterfront,
    gatedCommunity,
  }),
);

export const selectContentTemplateTypeModifiers = createSelector(
  selectAppliedFacets,
  selectProductType,
  selectHomeStatus,
  selectBrandId,
  (appliedFacetList, productType, homeStatus, brandId) => ({ appliedFacetList, productType, homeStatus, brandId }),
);

export const selectPaginationContext = createSelector(
  selectPage,
  selectPageSize,
  selectListingCount,
  selectSpotlightListingsCount,
  (pageNumber, pageSize, listingCount, spotlightListingsCount) => ({
    pageNumber,
    pageSize,
    listingCount,
    spotlightListingsCount,
  }),
);

export const pageCountSelector = createSelector(selectPageSize, selectListingCount, (pageSize, listingCount) =>
  Math.ceil(listingCount / pageSize),
);

export const moveInDateSelector = createSelector(
  selectHomeStatus,
  selectMoveInDateRange,
  (homeStatus, moveInDateRange) => ({
    homeStatus,
    moveInDateRange,
  }),
);

export const isAnyHousingTypeActiveSelector = createSelector(
  selectSingleFamily,
  selectLuxury,
  selectCondosAndTownHomes,
  isCustom,
  isBuildOnYourLot,
  selectHomeStatus,
  (singleFamily, luxury, condosAndTownHomes, custom, buildOnYourLot, homeStatus) =>
    Boolean(singleFamily || luxury || condosAndTownHomes || custom || buildOnYourLot || hasModelHome(homeStatus))
);

/**
 * selector used to get the facets' current state from the store
 * @param {object} state
 */
export const facetsSelector = createSelector(
  selectNatureAreas,
  selectPool,
  selectGolfCourse,
  selectGreen,
  selectSportsFacilities,
  selectParks,
  selectViews,
  selectWaterfront,
  selectGatedCommunity,
  selectRvGarage,
  selectReviews,
  selectConsumerPromos,
  selectPriceReduction,
  selectAdult,
  selectAgeRestricted,
  selectMinimumPrice,
  selectMaximumPrice,
  selectBathrooms,
  selectBedrooms,
  selectSchools,
  selectBrandId,
  selectGarages,
  selectMarketingStatus,
  selectPrimaryBedroom,
  selectStories,
  selectLivingAreas,
  selectSingleFamily,
  selectLuxury,
  selectCondosAndTownHomes,
  isCustomHomes,
  moveInDateSelector,
  selectCommunityName,
  selectHomeStatus,
  selectExcludeAdult,
  selectExcludeAgeRestricted,
  selectSorting,
  selectHomeOwnersAssociationFee,
  (
    natureAreas,
    pool,
    golfCourse,
    green,
    sportsFacilities,
    parks,
    views,
    waterfront,
    gatedCommunity,
    rvGarage,
    reviews,
    consumerPromos,
    priceReduction,
    adult,
    ageRestricted,
    minimumPrice,
    maximumPrice,
    bathrooms,
    bedrooms,
    schools,
    brand,
    garages,
    marketingStatus,
    primaryBedroom,
    stories,
    livingAreas,
    singleFamily,
    luxury,
    condosAndTownHomes,
    customHomes,
    moveInDate,
    communityName,
    homeStatus,
    excludeAdult,
    excludeAgeRestricted,
    sorting,
    hasHomeOwnersAssociationFee,
  ) => {
    const filters = {
      natureAreas,
      comingsoon: isComingSoon(marketingStatus),
      grandOpening: isGrandOpening(marketingStatus),
      active: isActive(marketingStatus),
      closeOut: isCloseOut(marketingStatus),
      pool,
      golfCourse,
      green,
      sportsFacilities,
      parks,
      views,
      waterfront,
      gatedCommunity,
      rvGarage,
      reviews,
      hotDeals: consumerPromos,
      priceReduction,
      moveInDate: isMoveInDateApplied(moveInDate),
      modelHomes: hasModelHome(homeStatus),
      adult,
      ageRestricted,
      price: minimumPrice || maximumPrice,
      bathrooms: Number(bathrooms),
      bedrooms: Number(bedrooms),
      schoolDistrict: Boolean(schools?.length),
      builderBrand: Number(brand),
      garages: Number(garages),
      primaryBedroom: Number(primaryBedroom),
      stories: Number(stories),
      livingAreas: Number(livingAreas),
      singleFamily,
      luxury,
      condosAndTownHomes,
      customHomes,
      communityName,
      exclude55: Boolean(excludeAdult && excludeAgeRestricted),
      sort: sorting?.sortBy,
      hasHomeOwnersAssociationFee,
    };

    return filters;
  },
);

/**
 * Selector that extends the facets object with formatted minimum and maximum price values.
 *
 * @function
 * @name extendedFacetsSelector
 * @param {Object} state - The Redux state.
 * @returns {Object} An object containing all facets along with formatted `minimumPrice` and `maximumPrice`.
 * @property {Object} facets - The original facets object from the state.
 * @property {string|null} minimumPrice - The formatted minimum price in USD, or `null` if not available.
 * @property {string|null} maximumPrice - The formatted maximum price in USD, or `null` if not available.
 */
export const extendedFacetsSelector = createSelector(
  facetsSelector,
  selectMinimumPrice,
  selectMaximumPrice,
  selectBrandName,
  selectMoveInDateRange,
  selectHomeStatus,
  selectProductType,
  (facets, minimumPrice, maximumPrice, brandName, moveInDateRange, homeStatus, productType) => {
    const formatCurrency = (value) =>
      value
        ? new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 0,
          }).format(value)
        : null;

    return {
      ...facets,
      minimumPrice: formatCurrency(minimumPrice),
      maximumPrice: formatCurrency(maximumPrice),
      brandName,
      moveInDateRange,
      homeStatus,
      productType,
    };
  },
);

/** this selector is intended to expose all the properties that should be included as common tracking properties.
 * the idea behind this is tha here should be placed the selectors/props that are usually used in tracking events.
 * then the consumers will filter and take the props they need, this will just serve the information.
 */
export const selectSearchResultFacetsTrackingCommonProperties = createSelector(
  selectLocation,
  selectBrandInformation,
  facetsSelector,
  selectPaginationContext,
  selectPrice,
  selectSchools,
  moveInDateSelector,
  (location, brandInformation, facetsList, paginationContext, price, schools, moveInDate) => {
    const { brandId, brandName } = brandInformation;
    const { pageNumber, listingCount } = paginationContext;
    return { location, brandId, brandName, facetsList, pageNumber, listingCount, price, schools, moveInDate };
  },
);

/**
 * This selector aims to expose all properties that can be used as a filter in a home.
 */
export const homeFactsSelectors = createSelector(
  selectRvGarage,
  selectBathrooms,
  selectBedrooms,
  selectGarages,
  selectPrimaryBedroom,
  selectStories,
  selectLivingAreas,
  (rvGarage, bathrooms, bedrooms, garages, primaryBedroom, stories, livingAreas) => {
    const filters = {
      rvGarage,
      bathrooms: Number(bathrooms),
      bedrooms: Number(bedrooms),
      garages: Number(garages),
      primaryBedroom: Number(primaryBedroom),
      stories: Number(stories),
      livingAreas: Number(livingAreas),
    };
    return filters;
  },
);

/**
 * This selector has the objective of displaying all the properties related to the filters in the SRP all filters menu.
 */
export const facetsPanelSelector = createSelector(
  communityAmenitiesSelector,
  selectPrice,
  homeFactsSelectors,
  brandIdAndSchoolsSelector,
  otherFiltersSelector,
  moveInDateSelector,
  housingOptionsSelector,
  communityStatusSelector,
  (
    communityAmenities,
    price,
    homeFacts,
    brandAndSchools,
    otherFilters,
    moveInDate,
    housingOptions,
    communityStatus,
  ) => {
    const filters = {
      amenities: communityAmenities,
      moveInDate: getMovingDateFilterApplied(moveInDate),
      price,
      homeFacts,
      brandAndSchools,
      housingOptions,
      communityStatus,
      otherFilters,
    };

    return filters;
  },
);

/**
 * This selector aims to display all the properties related to the filters in the popular search pills.
 * When the golf course pill is removed from the popular searches pills, please delete the amenities property from here.
 * Ticket where that pill will be removed: https://builderhomesite.atlassian.net/browse/NHS20-2391.
 */
export const selectLinkedPopularPillsFilters = createSelector(
  communityAmenitiesSelector,
  otherFiltersSelector,
  moveInDateSelector,
  housingOptionsSelector,
  selectMarketingStatus,
  (communityAmenities, otherFilters, moveInDate, housingOptions, marketingStatus) => {
    const filters = {
      amenities: communityAmenities,
      otherFilters,
      moveInDate: getMovingDateFilterApplied(moveInDate),
      housingOptions,
      comingsoon: isComingSoon(marketingStatus),
    };
    return filters;
  },
);

/**
 * This selectors maps the sorting value along with the result type value from Store.
 */
export const selectResultTypeAndSorting = createSelector(selectSorting, selectResultType, (sorting, resultType) => ({
  sorting,
  resultType,
}));

/**
 * This selector maps all the state properties that can drive an user search.
 */
export const selectSearchModifiers = createSelector(
  selectFeatures,
  selectFacts,
  selectLocation,
  selectProduct,
  selectResults,
  selectResultsModifiers,
  selectFetchResultListingsStatus,
  (feature, facts, location, product, results, modifiers, fetchResultsListingStatus) => ({
      feature,
      facts,
      location,
      product,
      results,
      modifiers,
      fetchResultsListingStatus,
  })
);

export const selectRectangularAreaLocation = (state) => {
  const location = selectLocation(state);

  if (location?.locationType !== LocationType.RectangularArea.toString()) {
    return null;
  }

  const { minimumLatitude, maximumLatitude, minimumLongitude, maximumLongitude } = location;
 
  if (![minimumLatitude, maximumLatitude, minimumLongitude,   maximumLongitude].every(Number.isFinite)) {
    return null;
  }

  return location;
};